import { captureException } from '@sentry/react'
import { TAppDispatch, TGetState } from 'store/types'
import { ErrorKeys } from 'consts/errorKeys'
import { ApiError, ReduxThunkError, isDraftLimitError } from 'utils/errors'
import { handleApiError } from 'store/utils'
import { generateAiImg_API } from 'api'
import slice from 'store/features/aiAvatar/slice'
import { aiAvatarSelectors } from 'store/features/aiAvatar/selectors'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { withAppThunks } from 'store/with-app-thunks'
import { AvatarSource } from 'store/types/aiAvatar'
import { IGenerateImagePayload } from 'store/types/aiImage'
import { convertAspectRatioToNumber } from 'utils/aspectRatio'
import {
  getSourceImageModel,
  getSourceImageProgressId,
  pickClosestAspectRatio,
  resolveAvatarAspectRatio
} from 'store/features/aiAvatar/utils'
import {
  AVATAR_ENHANCE_PROMPT,
  AVATAR_SOURCE_IMAGE_EXPECTED_DURATION_SECONDS,
  AVATAR_SOURCE_IMAGE_MODEL_CODE
} from 'store/features/aiAvatar/consts'
import { generationProgressService } from 'services/generationProgress'
import {
  removePendingSourceImageEntry,
  saveAssetPendingOp,
  savePendingSourceImageEntry
} from 'store/features/aiAvatar/sourceImageStorage'
import { resolveTargetAssetId } from 'store/features/aiAvatar/thunks/sourceImage'

const { actions } = slice

/**
 * "Enhance quality" for the AI Avatar source image.
 *
 * Sends the current source image back through the AI image generator with a
 * fixed prompt (AVATAR_ENHANCE_PROMPT) that sharpens/relights/denoises AND
 * reframes the subject into a front-facing head-and-shoulders talking-head
 * composition — while LOCKING identity (same face, features, hair, clothing),
 * so the result is a cleaner, avatar-ready version of the SAME person.
 *
 * Only meaningful for the user's own images (UPLOAD / My Files); public avatars
 * are pre-rendered assets, so the button is hidden for them (and the guard here
 * is the backstop).
 *
 * ASYNC DELIVERY: like the AI Image tab, `generateAiImg_API` returns fast with a
 * project-file id; the enhanced image arrives LATER on the shared
 * `type:'ai-image'` socket, correlated by that id. We stash it as
 * `pendingSourceImageId` and let `handleAIImgSocketData` apply the result.
 */
const enhanceAvatarImage = () =>
  withAppThunks(
    appThunks =>
      async (dispatch: TAppDispatch, getState: TGetState): Promise<void> => {
        const avatar = aiAvatarSelectors.avatar(getState())
        if (!avatar?.url) return
        if (
          avatar.source !== AvatarSource.UPLOAD &&
          avatar.source !== AvatarSource.MY_FILES
        ) {
          return
        }

        // Loader over the current preview starts immediately; the socket branch
        // clears it on completion (do NOT clear in a `finally`, that would drop
        // the loader right after kickoff while the socket is still pending).
        dispatch(actions.setAvatarLoading(true))
        // Set once the progress ticker starts, so the catch can unwind it.
        let startedImageId: number | null = null
        try {
          const projectId = await dispatch(
            appThunks.projectThunks.getOrCreateProjectId()
          )

          // Aspect ratios / cost live on the aiImage model catalog; ensure it's
          // loaded (idempotent — returns the cached list after the first call).
          const modelsData = await dispatch(appThunks.aiImageThunks.getModels())
          const model = getSourceImageModel(modelsData)

          // Enhance keeps the frame, so send the source image's own ratio,
          // snapped to the nearest ratio the model supports; fall back to the
          // effective avatar ratio when the natural pixel size is unknown.
          const fallbackRatio = resolveAvatarAspectRatio(
            aiAvatarSelectors.avatarAspectRatio(getState()),
            projectDataSelectors.defaultDimensions(getState()).aspectRatioString
          )
          const sourceRatioNumber =
            avatar.width && avatar.height
              ? avatar.width / avatar.height
              : convertAspectRatioToNumber(fallbackRatio)
          const aspectRatio =
            pickClosestAspectRatio(
              sourceRatioNumber,
              model?.possibleAspectRatios ?? []
            ) ?? fallbackRatio

          const payload: IGenerateImagePayload = {
            model: AVATAR_SOURCE_IMAGE_MODEL_CODE,
            prompt: AVATAR_ENHANCE_PROMPT,
            refImages: [avatar.url],
            aspectRatio,
            meta: { sourceProjectId: projectId },
            videoProjectId: projectId
          }

          const { data } = await generateAiImg_API(payload)
          const imageId = data.createdAiImageProjectIds?.[0]

          // No project created → nothing will arrive on the socket; release now.
          if (imageId === undefined) {
            dispatch(actions.setAvatarLoading(false))
            return
          }

          // aspectRatio: null → Enhance preserves the current avatar ratio.
          dispatch(
            actions.setPendingSourceImage({ id: imageId, aspectRatio: null })
          )
          // Simulated percentage for the preview's progress ring (persisted, so
          // it resumes after a reload); the socket/reconcile terminals
          // complete/stop it by the same namespaced id.
          generationProgressService.start(
            getSourceImageProgressId(imageId),
            String(imageId),
            AVATAR_SOURCE_IMAGE_EXPECTED_DURATION_SECONDS,
            false,
            0
          )
          startedImageId = imageId
          // Persist the op so it survives navigation/reloads (see
          // sourceImageStorage): a fresh form (case 1) uses the single-slot
          // entry; a regen form (case 2) uses the assetId-keyed ops map, so
          // deselecting the clip no longer abandons the paid op.
          if (aiAvatarSelectors.targetMediaItemId(getState()) === null) {
            savePendingSourceImageEntry({
              videoProjectId: Number(projectId),
              imageId,
              targetAspectRatio: null,
              avatar,
              ratioOverride: aiAvatarSelectors.avatarAspectRatio(getState()),
              createdAt: Date.now()
            })
          } else {
            const assetId = resolveTargetAssetId(getState())
            if (assetId) {
              saveAssetPendingOp(assetId, {
                imageId,
                targetAspectRatio: null,
                createdAt: Date.now()
              })
            }
          }
        } catch (error) {
          // Draft-limit 409: already surfaced by createProject with its own
          // upgrade popup — don't mask it with a generic error popup.
          if (!isDraftLimitError(error)) {
            if (!(error instanceof ApiError)) {
              captureException(new ReduxThunkError(error))
            }
            dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
          }
          // The socket won't fire on failure, so unwind loader + correlation
          // (and the persisted entry, when the failure hit after the write).
          dispatch(actions.setAvatarLoading(false))
          dispatch(actions.clearPendingSourceImage())
          removePendingSourceImageEntry()
          if (startedImageId !== null) {
            generationProgressService.stop({
              mediaItemId: getSourceImageProgressId(startedImageId),
              shouldRemoveFromStorage: true
            })
          }
        }
      }
  )

export const enhanceThunks = { enhanceAvatarImage }
